import { useState } from "react";
import type { ReactNode } from "react";
import { Popover } from "antd";
import type { TooltipPlacement } from "antd/es/tooltip";
import FilterTile from "./FilterTile";
import type { Option } from "./FilterOptions";
import { toTitleCase } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import {
  buildRelativeDateFilterOptions,
  createDateFilter,
  hasFilterValue,
  getInitialNumericValues,
  getInitialDateValues,
  parseAndCreateNumericFilter,
} from "./filtersUtil";
import type { FilterType, NumericCondition } from "./filterTypes";
import FilterConditions from "./FilterConditions";

type FilterDropdownProps = {
  filterKey: string;
  dataType: FilterType;
  filterOptions?: Option[];
  onDelete: () => void;
  setFilters: (filters: Record<string, any>) => void;
  currentFilters: Record<string, any>;
  showTitle?: boolean;
  placement?: TooltipPlacement;
  alignOffset?: { offset: [number, number] };
  tileWidth?: string;
  columnsTitleCreator?: (column: string) => string;
  useAlternativeLayout?: boolean;
  hideFooter?: boolean;
  disabled?: boolean;
  showAsAvailable?: boolean;
  triggerContent?: (open: boolean) => ReactNode;
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
};

export default function FilterDropdown({
  filterKey,
  dataType,
  filterOptions,
  onDelete,
  setFilters,
  currentFilters,
  showTitle = false,
  placement = "bottomLeft",
  alignOffset = { offset: [0, 10] },
  tileWidth = "235px",
  columnsTitleCreator,
  useAlternativeLayout = false,
  hideFooter = false,
  disabled = false,
  showAsAvailable = false,
  triggerContent,
  getPopupContainer,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [localSelectedValues, setLocalSelectedValues] = useState([]);

  const handleOpenChange = (newOpen) => {
    if (newOpen) {
      setLocalSelectedValues(currentFilters[filterKey] || []);
    }
    setOpen(newOpen);
  };

  const handleApply = (values) => {
    const newFilters = { ...currentFilters, [filterKey]: values };
    setFilters(newFilters);
    setOpen(false);
  };

  const handleDelete = () => {
    onDelete();
    setOpen(false);
  };

  const handleApplyNumericFilter = (
    filterKey: string,
    condition: NumericCondition,
    value: any,
  ) => {
    const numericCondition = parseAndCreateNumericFilter(condition, value);
    setFilters({ ...currentFilters, [filterKey]: [numericCondition] });
    setOpen(false);
  };

  const handleApplyDateFilter = (filterKey: string, filterData: any) => {
    let dateCondition;
    if (filterData.condition === "no_data") {
      dateCondition = createDateFilter("no_data");
    } else if (filterData.condition === "between" && filterData.range) {
      const relativeOptions = buildRelativeDateFilterOptions(
        filterData.mode,
        filterData.relativePreset,
      );

      dateCondition = createDateFilter(
        "between",
        undefined,
        filterData.range[0],
        filterData.range[1],
        relativeOptions,
      );
    } else if (filterData.date) {
      dateCondition = createDateFilter(filterData.condition, filterData.date);
    }

    if (dateCondition) {
      setFilters({ ...currentFilters, [filterKey]: [dateCondition] });
      setOpen(false);
    }
  };

  const selectedValues = open
    ? localSelectedValues
    : currentFilters[filterKey] || [];

  return (
    <Popover
      content={
        <div className="min-w-[210px] max-w-[350px]">
          {showTitle && (
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
              <h2 className="text-sm font-bold">
                {columnsTitleCreator
                  ? columnsTitleCreator(filterKey)
                  : toTitleCase(filterKey)}
              </h2>
            </div>
          )}
          <FilterConditions
            filterOptions={filterOptions}
            currentFilters={currentFilters}
            selectedValues={selectedValues}
            filterKey={filterKey}
            dataType={dataType}
            handleApply={handleApply}
            handleDelete={handleDelete}
            getInitialNumericValues={() =>
              getInitialNumericValues(currentFilters[filterKey])
            }
            getInitialDateValues={() =>
              getInitialDateValues(currentFilters[filterKey])
            }
            handleApplyNumericFilter={handleApplyNumericFilter}
            handleApplyDateFilter={handleApplyDateFilter}
            setOpen={setOpen}
            setLocalSelectedValues={setLocalSelectedValues}
            useAlternativeLayout={useAlternativeLayout}
            hideFooter={hideFooter}
            disabled={disabled}
          />
        </div>
      }
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement={placement}
      arrow={false}
      align={alignOffset}
      getPopupContainer={getPopupContainer}
      destroyOnHidden
    >
      {triggerContent ? (
        triggerContent(open)
      ) : (
        <div style={{ width: tileWidth }}>
          <FilterTile
            filter={filterKey}
            dataType={dataType}
            isSelected={open}
            hasFilterValues={hasFilterValue(currentFilters[filterKey])}
            filterValue={selectedValues}
            columnsTitleCreator={columnsTitleCreator}
            disabled={disabled}
            showAsAvailable={showAsAvailable}
          />
        </div>
      )}
    </Popover>
  );
}
